import { DataGrid } from '@mui/x-data-grid';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import useMediaPlansTableColumns from './hooks/useMediaPlansTableColumns';
import CreatePlanButton from '../CreatePlanButton/CreatePlanButton';
import usePlanStore from '../../../../store/planStore';
import TableSearchbar from './TableSearchbar/TableSearchbar';
import useMediaPlansInfinite from './hooks/useMediaPlansInfinite';
import TotalsFooter from './TotalsFooter/TotalsFooter';
import DeletePlanConfirmationModal from './DeletePlanConfirmationModal/DeletePlanConfirmationModal';
import ComparedPlansBar from './ComparedPlansBar/ComparedPlansBar';

function CustomNoRowsOverlay() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'gray',
        fontSize: '16px',
      }}
    >
      <Typography variant="body1" textAlign="center">
        There are no media plans created yet. <br /> Click the blue &quot;Create
        plan&quot; button and create your first media plan.
      </Typography>
    </Box>
  );
}

export default function MediaPlansTable() {
  const { setIsPlanCreating } = usePlanStore();
  const handleCreatePlan = () => setIsPlanCreating(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const { comparedPlans } = usePlanStore();

  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const { palette } = useTheme();
  const { columns, deletePlan, deletedId } =
    useMediaPlansTableColumns(setOpenDeleteModal);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const {
    plans,
    fetchNextPage,
    hasNextPage,
    total,
    isFetchingNextPage,
    isLoading,
  } = useMediaPlansInfinite(searchTerm);

  const debouncedSetSearchTerm = useMemo(
    () => debounce((query: string) => setSearchTerm(query), 300),
    []
  );

  const handleSearch = (query: string) => debouncedSetSearchTerm(query);

  const handleScroll = useCallback(() => {
    if (!gridRef.current || !hasNextPage || isFetchingNextPage) return;

    const scrollContainer = gridRef.current.querySelector(
      '.MuiDataGrid-virtualScroller'
    );
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

    if (scrollTop + clientHeight >= scrollHeight - 150) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!gridRef.current) return undefined;

    const scrollContainer = gridRef.current.querySelector(
      '.MuiDataGrid-virtualScroller'
    );
    if (!scrollContainer) return undefined;

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const handleDeletePlan = () => {
    deletePlan(deletedId ?? '');
    setOpenDeleteModal(false);
  };

  const planIds = useMemo(() => Object.keys(comparedPlans), [comparedPlans]);

  const handleComparePlans = () => {
    const url = new URL(
      `/media-planning/compare?ids=${planIds.join(',')}`,
      window.location.origin
    );
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Stack width="100%" px="20px">
        <Stack
          flexDirection="row"
          alignItems="center"
          gap="32px"
          width="100%"
          mb="34px"
          mt="32px"
        >
          <TableSearchbar onSearch={handleSearch} />
          <CreatePlanButton handleCreatePlan={handleCreatePlan} />
        </Stack>
        <Box sx={{ height: 600, width: '100%' }} ref={gridRef}>
          <DataGrid
            rows={plans}
            columns={columns}
            rowHeight={64}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 !== 0 ? 'highlighted' : ''
            }
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: 'none',
                px: '20px',
                color: palette.mediaPlanTable.textColor,
                fontSize: '14px',
                fontWeight: 600,
              },
              '& .MuiDataGrid-columnHeader': {
                px: '20px',
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'uppercase',
                },
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: `1px solid ${palette.mediaPlanTable.headerDivider}`,
              },
              '& .MuiDataGrid-row': {
                borderBottom: 'none',
              },
              '.MuiDataGrid-iconButtonContainer': {
                ml: 1,
              },
              backgroundColor: 'primary.contrastText',
              '& .highlighted': {
                backgroundColor: 'mediaPlanTable.background',
              },
            }}
            hideFooter
            loading={isLoading || isFetchingNextPage}
            disableRowSelectionOnClick
            components={{
              NoRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        </Box>
        <TotalsFooter total={total} count={plans.length} />
        <ComparedPlansBar onCompare={handleComparePlans} />
      </Stack>
      <DeletePlanConfirmationModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleDeletePlan}
      />
    </>
  );
}
